// React
import React, { useState,  useRef, useEffect } from 'react';
import nextId from 'react-id-generator';

// Styles
import styles from './AddNew.module.scss';

// Material UI
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Third party
import { trackPromise } from 'react-promise-tracker';

// Services
import { saveData } from '../../../services/taxonomies';

// Shared
import eventBus from '../../../shared/eventBus';
import {
  dialog as dialogStyles,
  dialogTitle as dialogTitleStyles,
  dialogContent as dialogContentStyles,
  dialogActions as dialogActionsStyles
} from '../../../shared/theme';

// Hooks
import useMounted from '../../../hooks/useMounted';

// Custom styles
const useDialogStyles = makeStyles(dialogStyles);
const useDialogTitleStyles = makeStyles(dialogTitleStyles);
const useDialogContentStyles = makeStyles(dialogContentStyles);
const useDialogActionsStyles = makeStyles(dialogActionsStyles);

export default function AddNew({
  endpoint,
  inputParameter,
  taxonomyName,
  title,
  label,
  getInputs,
  getRows,
  size,
  disabled,
  dependOn = null
}) {

  // State that manage the modal opened/closed
  const [open, setOpen] = useState(false);

  // The form ID (random)
  const formId = nextId();

  // Inputs
  const [inputs, setInputs] = useState(getInputs(null, true));

  // If the component is mounted
  const mounted = useMounted();

  // Values from the parent/required fields
  const [dependOnValue, setDependOnValue] = useState([]);

  // Disabled state
  const [isDisabled, setDisabled] = useState(disabled);

  // Keep reference to the inital passed function (we don't need to watch its changes, just need to invoke)
  const getInputsRef = useRef(getInputs);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (event) => {
    if (event.target.id !== formId) return;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    const formData = new FormData(form);

    const promise = saveData({
      itemHash: 'new',
      endpoint,
      formData
    });

    trackPromise(
      promise.then((response) => {
        if (response.succeeded) {
          const newRows = getRows([response.data]);

          eventBus.dispatch('taxonomyAdded', {
            taxonomyName: taxonomyName,
            inputParameter: inputParameter,
            newRows: newRows
          });

          setOpen(false);
        } else if (response.errors && response.errors.length > 0) {
          alert(response.errors.join('\n'));
        } else {
          alert('There was an error with your submission');
        }
      })
    );
  };

  // Parent/required fields have changed (values)
  useEffect(() => {
    if (dependOn && typeof dependOn === 'object' && dependOn.length > 0) {
      // Add event to each parent/required field
      dependOn.forEach((dependOn_) => {
        eventBus.on(`formInputChange_${dependOn_.control}`, (data_) => {
          if (mounted.current) {
            const filter = dependOn_.filter;
            const control = dependOn_.control;
            const value_ = data_.value;

            // Set parent/required fields values
            setDependOnValue((currentDependOnValue) => {
              let newDependOnValue = null;

              // Index to update
              const index = currentDependOnValue.findIndex(
                (item) => item.filter === filter
              );

              // Update item
              if (index >= 0) {
                newDependOnValue = currentDependOnValue.map((item) =>
                  item.filter === filter ? { ...item, value: value_ } : item
                );
              }
              // New item
              else {
                newDependOnValue = [
                  ...currentDependOnValue,
                  { filter, control, value: value_ }
                ];
              }

              // How many parent/required fields are populated
              const newDependOnValueCounter = newDependOnValue.filter(
                (item) => item.value && item.value !== ''
              ).length;

              // Enable the field in case all parent/required fields are populated
              if (newDependOnValueCounter === dependOn.length) {
                setDisabled(false);
              }
              // Disable the field in case not all parent/required fields are populated
              else {
                setDisabled(true);
              }

              // Return new values/state
              return newDependOnValue;
            });
          }
        });
      });
    }
  }, [dependOn, mounted]);

  // Component did mount (get data)
  useEffect(() => {
    const data = {};

    dependOnValue.forEach((item) => {
      data[item.control] = item.value;
    });

    if (data) {
      setInputs(getInputsRef.current(data, true));
    }
  }, [dependOnValue]);

  // Custom styles
  const dialogClasses = useDialogStyles();
  const dialogTitleClasses = useDialogTitleStyles();
  const dialogContentClasses = useDialogContentStyles();
  const dialogActionsClasses = useDialogActionsStyles();

  return (
    <Grid item xs={size.xs} md={size.md} lg={size.lg} xl={size.xl}>
      <Button
        color="primary"
        onClick={handleOpen}
        disabled={isDisabled}
        id={`add-new-${inputParameter}`}>
        {label}
      </Button>
      <Dialog
        classes={dialogClasses}
        open={open}
        onClose={handleClose}
        maxWidth="md"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title" classes={dialogTitleClasses}>
          <Typography variant="subtitle1" component="div">
            Add New
          </Typography>
          <Typography variant="h2" component="div">
            {title}
          </Typography>
        </DialogTitle>
        <DialogContent classes={dialogContentClasses}>
          <form id={formId} onSubmit={handleSubmit}>
            {inputs &&
              inputs.map((row, i) => {
                return (
                  <div key={i} className={styles.row}>
                    <Grid container spacing={4}>
                      {row &&
                        row.map((input, j) => {
                          if (input.hidden) {
                            return <div key={j}>{input.component}</div>;
                          }
                          return (
                            <Grid key={j} item xs={12}>
                              <Grid container spacing={1} alignItems="center">
                                <Grid item xs={12}>
                                  {input.component}
                                </Grid>
                                {input.addNew}
                              </Grid>
                            </Grid>
                          );
                        })}
                    </Grid>
                  </div>
                );
              })}
          </form>
        </DialogContent>
        <DialogActions classes={dialogActionsClasses}>
          <Button
            onClick={handleClose}
            disableElevation={true}
            color="secondary"
            variant="outlined"
            size="large">
            cancel
          </Button>
          <Button
            disableElevation={true}
            color="secondary"
            variant="contained"
            size="large"
            type="submit"
            form={formId}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
